import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export function useShiftMasters() {
  const { actingOrgId } = useAuth();
  const { data: locs = [] } = useQuery({ queryKey: ["r-locs", actingOrgId], queryFn: async () => (await api.get("/org/masters/locations")).data });
  const { data: depts = [] } = useQuery({ queryKey: ["r-depts", actingOrgId], queryFn: async () => (await api.get("/org/masters/departments")).data });
  return { locs, depts };
}

export function ScopePicks({ label, hint, items, value, onChange, testid }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const all = items.length > 0 && value.length === items.length;
  return (
    <div className="min-w-[220px] flex-1">
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold text-slate-800">{label} <span className="font-normal text-slate-500">({value.length ? `${value.length} selected` : hint})</span></p>
        <button type="button" onClick={() => onChange(all ? [] : items.map((x) => x.id))} className="rounded-md border border-indigo-200 px-2 py-0.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50" data-testid={`${testid}-select-all`}>
          {all ? "Clear" : "Select all"}
        </button>
      </div>
      <div className="flex max-h-36 flex-wrap gap-x-4 gap-y-2 overflow-y-auto rounded-md border border-slate-200 p-2.5" data-testid={testid}>
        {items.map((x) => <label key={x.id} className="flex items-center gap-2 text-sm text-slate-800"><Checkbox checked={value.includes(x.id)} onCheckedChange={() => toggle(x.id)} data-testid={`${testid}-${x.id}`} /> {x.name}</label>)}
        {!items.length && <span className="text-sm text-slate-500">None in Masters</span>}
      </div>
    </div>
  );
}

export const placedToast = (data) => {
  const parts = [data.copied.length && `${data.copied.length} added`, data.updated.length && `${data.updated.length} given the new departments`,
    data.skipped.length && `${data.skipped.length} already there`].filter(Boolean);
  toast.success(parts.join(" · ") || "Done");
};

export function CopyShiftsDialog({ open, onOpenChange, shiftIds, onDone }) {
  const { locs, depts: allDepts } = useShiftMasters();
  const [loc, setLoc] = useState([]);
  const [depts, setDepts] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/roster/shifts/copy", { shift_ids: shiftIds, location_ids: loc, department_ids: depts });
      placedToast(data);
      setLoc([]); setDepts([]);
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="copy-shifts-dialog">
        <DialogHeader>
          <DialogTitle>Assign {shiftIds.length} shift(s) to locations and departments</DialogTitle>
          <DialogDescription>A copy is placed in every location you pick and the originals stay where they are. If a location already has that code, the chosen departments are added to it instead.</DialogDescription>
        </DialogHeader>
        <ScopePicks label="Locations" hint="pick at least one" items={locs} value={loc} onChange={setLoc} testid="copy-loc" />
        <ScopePicks label="Departments" hint="all departments" items={allDepts} value={depts} onChange={setDepts} testid="copy-dept" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="copy-shifts-cancel">Cancel</Button>
          <Button disabled={!loc.length || busy} onClick={submit} data-testid="copy-shifts-submit">Assign shifts</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
